import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { CheckCircle, XCircle, Clock, Mail, Shield, RefreshCw, AlertTriangle, Settings2 } from 'lucide-react';

const statusConfig = {
  email_pending: { label: 'Email en attente', color: 'bg-blue-100 text-blue-700', icon: Mail },
  pending_approval: { label: 'Approbation requise', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approuve', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Refuse', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const AccessRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [approveDialog, setApproveDialog] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [selectedRole, setSelectedRole] = useState('auditeur');
  const [rejectReason, setRejectReason] = useState('');
  const [oauthSettings, setOauthSettings] = useState(null);
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [processing, setProcessing] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = filter === 'pending' ? '/access-requests/pending' : '/access-requests/all';
      const resp = await api.get(endpoint);
      setRequests(resp.data.requests || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await api.get('/auth/oauth-settings');
      setOauthSettings(resp.data);
      setSettingsForm(resp.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
    fetchSettings();
  }, [fetchRequests, fetchSettings]);

  const handleApprove = async () => {
    if (!approveDialog) return;
    setProcessing(true);
    try {
      await api.put(`/access-requests/${approveDialog.id}/approve`, { role: selectedRole });
      toast.success(`Demande de ${approveDialog.email} approuvee`);
      setApproveDialog(null);
      setSelectedRole('auditeur');
      fetchRequests();
    } catch (err) {
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectDialog) return;
    setProcessing(true);
    try {
      await api.put(`/access-requests/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success(`Demande de ${rejectDialog.email} refusee`);
      setRejectDialog(null);
      setRejectReason('');
      fetchRequests();
    } catch (err) {
      toast.error('Erreur lors du refus');
    } finally {
      setProcessing(false);
    }
  };

  const handleSaveSettings = async () => {
    setProcessing(true);
    try {
      await api.put('/auth/oauth-settings', settingsForm);
      toast.success('Configuration OAuth mise a jour');
      setSettingsDialog(false);
      fetchSettings();
    } catch (err) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6" data-testid="access-requests-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Demandes d'acces Microsoft</h2>
          <p className="text-gray-500 text-sm mt-1">Gestion des demandes de connexion via Microsoft OAuth</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsDialog(true)}
            data-testid="oauth-settings-btn"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Configuration OAuth
          </Button>
          <Button variant="outline" size="sm" onClick={fetchRequests} data-testid="refresh-requests-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* OAuth Status Card */}
      {oauthSettings && (
        <Card>
          <CardContent className="py-3 flex items-center gap-4 text-sm">
            <Shield className="h-5 w-5 text-blue-500" />
            <span>
              Microsoft OAuth : {oauthSettings.microsoft_configured ? (
                <Badge variant="outline" className="ml-1 bg-green-50 text-green-700 border-green-200">Configure</Badge>
              ) : (
                <Badge variant="outline" className="ml-1 bg-red-50 text-red-700 border-red-200">Non configure</Badge>
              )}
            </span>
            <span className="text-gray-400">|</span>
            <span>Domaines autorises : {(oauthSettings.allowed_domains || []).join(', ') || 'Tous'}</span>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex gap-2">
        <Button
          variant={filter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('pending')}
          data-testid="filter-pending-btn"
        >
          En attente
        </Button>
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilter('all')}
          data-testid="filter-all-btn"
        >
          Toutes
        </Button>
      </div>

      {/* Requests List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center text-gray-500">Chargement...</div>
          ) : requests.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <Clock className="h-10 w-10 mx-auto text-gray-300 mb-3" />
              Aucune demande {filter === 'pending' ? 'en attente' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="requests-table">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium text-gray-600">Utilisateur</th>
                    <th className="text-left p-3 font-medium text-gray-600">Email</th>
                    <th className="text-left p-3 font-medium text-gray-600">Domaine</th>
                    <th className="text-left p-3 font-medium text-gray-600">Statut</th>
                    <th className="text-left p-3 font-medium text-gray-600">Date</th>
                    <th className="text-right p-3 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((req) => {
                    const cfg = statusConfig[req.status] || statusConfig.email_pending;
                    const StatusIcon = cfg.icon;
                    return (
                      <tr key={req.id} className="border-b hover:bg-gray-50" data-testid={`request-row-${req.id}`}>
                        <td className="p-3 font-medium">
                          {req.firstName} {req.lastName}
                        </td>
                        <td className="p-3 text-gray-600">{req.email}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">{req.domain}</Badge>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{formatDate(req.requestedAt)}</td>
                        <td className="p-3 text-right">
                          {req.status === 'pending_approval' && (
                            <div className="flex gap-1 justify-end">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-xs h-8"
                                onClick={() => setApproveDialog(req)}
                                data-testid={`approve-btn-${req.id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Approuver
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-8"
                                onClick={() => setRejectDialog(req)}
                                data-testid={`reject-btn-${req.id}`}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                Refuser
                              </Button>
                            </div>
                          )}
                          {req.status === 'approved' && req.assignedRole && (
                            <Badge className="bg-green-100 text-green-700">{req.assignedRole}</Badge>
                          )}
                          {req.status === 'rejected' && (
                            <span className="text-xs text-red-500">{req.rejectionReason || 'Refuse'}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve Dialog */}
      <Dialog open={!!approveDialog} onOpenChange={() => setApproveDialog(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approuver la demande</DialogTitle>
          </DialogHeader>
          {approveDialog && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Approuver l'acces pour <strong>{approveDialog.firstName} {approveDialog.lastName}</strong> ({approveDialog.email}) ?
              </p>
              <div className="space-y-2">
                <Label>Role a attribuer</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auditeur">Auditeur</SelectItem>
                    <SelectItem value="Comptable">Comptable</SelectItem>
                    <SelectItem value="Dispatcheur">Dispatcheur</SelectItem>
                    <SelectItem value="Editeur">Editeur</SelectItem>
                    <SelectItem value="Admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(null)}>Annuler</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove} disabled={processing} data-testid="confirm-approve-btn">
              {processing ? 'Approbation...' : 'Confirmer l\'approbation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={!!rejectDialog} onOpenChange={() => setRejectDialog(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
          </DialogHeader>
          {rejectDialog && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Refuser l'acces pour <strong>{rejectDialog.firstName} {rejectDialog.lastName}</strong> ({rejectDialog.email}) ?
              </p>
              <div className="space-y-2">
                <Label>Raison du refus (optionnel)</Label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Raison..."
                  data-testid="reject-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(null)}>Annuler</Button>
            <Button variant="destructive" onClick={handleReject} disabled={processing} data-testid="confirm-reject-btn">
              {processing ? 'Refus...' : 'Confirmer le refus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OAuth Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent className="max-w-lg" data-testid="oauth-settings-dialog">
          <DialogHeader>
            <DialogTitle>Configuration OAuth Microsoft</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Microsoft OAuth actif</Label>
              <Button
                variant={settingsForm.microsoft_enabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSettingsForm(prev => ({ ...prev, microsoft_enabled: !prev.microsoft_enabled }))}
                data-testid="toggle-microsoft-enabled"
              >
                {settingsForm.microsoft_enabled ? 'Active' : 'Desactive'}
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Domaines autorises (separes par virgule)</Label>
              <Input
                value={(settingsForm.allowed_domains || []).join(', ')}
                onChange={(e) => setSettingsForm(prev => ({
                  ...prev,
                  allowed_domains: e.target.value.split(',').map(d => d.trim()).filter(Boolean),
                }))}
                placeholder="baobabco.net"
                data-testid="allowed-domains-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role par defaut</Label>
              <Select
                value={settingsForm.default_role || 'auditeur'}
                onValueChange={(v) => setSettingsForm(prev => ({ ...prev, default_role: v }))}
              >
                <SelectTrigger data-testid="default-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auditeur">Auditeur</SelectItem>
                  <SelectItem value="Comptable">Comptable</SelectItem>
                  <SelectItem value="Dispatcheur">Dispatcheur</SelectItem>
                  <SelectItem value="Editeur">Editeur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Verification email obligatoire</Label>
              <Button
                variant={settingsForm.require_email_verification ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSettingsForm(prev => ({ ...prev, require_email_verification: !prev.require_email_verification }))}
              >
                {settingsForm.require_email_verification ? 'Oui' : 'Non'}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <Label>Approbation admin obligatoire</Label>
              <Button
                variant={settingsForm.require_admin_approval ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSettingsForm(prev => ({ ...prev, require_admin_approval: !prev.require_admin_approval }))}
              >
                {settingsForm.require_admin_approval ? 'Oui' : 'Non'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialog(false)}>Annuler</Button>
            <Button onClick={handleSaveSettings} disabled={processing} data-testid="save-oauth-settings-btn">
              {processing ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessRequests;
